import { FC, useContext, useEffect, useRef, useState } from "react";

import { ToastContext } from "src/context/toastContext";
import useFetch from "src/hooks/useFetch";

import { ACCEPTED_PRECERT_FILE_EXTENSIONS, MAX_FILE_SIZE_IN_BYTES } from "src/constants";
import { ToastTypes } from "src/types";
import { APIEndpoints } from "src/types/apiTypes";
import { UploadClinicalInfoProps } from "./types";
import { DocumentAPIResponse } from "src/pages/precert/types";



const UploadClinicalModal: FC<UploadClinicalInfoProps> = ({ caseId, precertId, isModalVisible, hideModal, addSkipForNow }): JSX.Element => {
  const [uploadFile, changeUploadFile] = useState<FileList | null>(null);
  const [paValue, changePAValue] = useState<boolean>(true);
  const [uploadFileInputError, changeUploadFileInputError] = useState<string | null>(null);

  const ref = useRef<HTMLInputElement>(null);
  const { showToast } = useContext(ToastContext);





  const { loading: uploadFileLoading, error: uploadFileError, data: uploadFileResponse, mutate: uploadCaseDocument } =
    useFetch<DocumentAPIResponse>(APIEndpoints.UPLOAD_CASE_FILE);

  useEffect(() => {
    if (uploadFileResponse || uploadFileError) {
      if(uploadFileError) {
        changeUploadFile(null);
        reset();
      }
      if(uploadFileResponse) {
        // const { message } = uploadFileResponse;
        showToast(["case updated"], ToastTypes.SUCCESS);
        reset();
        hideModal(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileResponse, uploadFileError]);

  const completeOnMyBehalf = () => {
    if (uploadFile) {
      const formData = new FormData();
      formData.append('file', uploadFile[0]);
      formData.append("type", "clinicals");
      uploadCaseDocument(formData, { caseId });
    }
  };





  const reset = () => {
    if (ref && ref.current) {
      ref.current.value = ""
    }
  };

  const clearDataAndForm = () => {
    changeUploadFile(null);
    reset();
  };

  const validateAndShowToast = (file: File) => {
    const caseFileExt = `.${file.name.split(".").pop()}`;
    const isExtAccepted = ACCEPTED_PRECERT_FILE_EXTENSIONS[caseFileExt];
    if (typeof isExtAccepted === "string") {
      const sizeInMBs = Number((file.size / (1024 * 1024)).toFixed(2));
      if (sizeInMBs <= 25) {
        return true;
      } else {
        showToast(["Document size should not exceed 25mb."], ToastTypes.WARNING);
      }
    } else {
      showToast(
        [`Not an acceptable format. The accepted formats are ${Object.keys(ACCEPTED_PRECERT_FILE_EXTENSIONS).toString()}`],
        ToastTypes.ERROR
      );
    }
    return false;
  };

  const handleConfirmAndExecute = (title: string, action: Function) => {
    // const result = window.confirm(title);
    // if (result)
    action();
  };

  const ConfirmAlert = (title: string, funcName: string) => {
    if (uploadFile) {
      if (validateAndShowToast(uploadFile[0])) {
        handleConfirmAndExecute(title, completeOnMyBehalf);
      }
    }
  };

  const onUplaodCalled = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const caseFileExt = `.${files[0].name.split(".").pop()}`;
      const isExtAccepted = Object.keys(ACCEPTED_PRECERT_FILE_EXTENSIONS).find((ext) => caseFileExt === ext);
      if (!isExtAccepted) {
        changeUploadFileInputError(`Not an acceptable format`);
        ref.current && (ref.current.value = "") // reset file input
      } else if (files[0].size > MAX_FILE_SIZE_IN_BYTES) {
        changeUploadFileInputError("File size exceeds 25MB limit");
        ref.current && (ref.current.value = "") // reset file input
      } else {
        changeUploadFileInputError(null);
        changeUploadFile(files)
      }
    }
  }

  return (
    <div
      className={`modal fade ${isModalVisible ? 'show show-modal' : ''}`}
      id="transferNotNowModal"
      tabIndex={-1}
      data-bs-backdrop="true"
      aria-hidden="true"
      onClick={() => hideModal(false)}
    >
      <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <p className="modal-title fw-bold" style={{ color: '#008853', fontSize: '1.875rem' }}>
              Upload Clinical Information
            </p>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => hideModal(false)}></button>
          </div>

          <div className="modal-body">
            <p>
              My QUTENZA Connect can help by processing potential precertification or prior authorization
              requests on your behalf. For assistance, fax (855.454.8746) or upload relevant supporting
              clinical chart documentation related to the patient's indication. In particular ensure the
              following elements are included:
            </p>
            <ul>
              <li>Visit notes within last 12 months</li>
              <li>List all tried and failed and/or did not tolerate medications</li>
              <li>The ICD-10/diagnosis code must be in the clinical notes</li>
              <li>The provider listed on the case must match provider in clinical notes</li>
            </ul>
            <p>
              If approved, the prior authorization will be sent to you and will be forwarded to your
              pre-selected specialty pharmacy (if you have chosen to use a specialty pharmacy in the
              QUTENZA network):
            </p>

            <form>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="pa" id="paYes" checked={paValue} onChange={(e) => changePAValue(true)} />
                <label className="form-check-label fw-bold" htmlFor="paYes">
                  Complete precertification or prior authorization on behalf of my office (fax or upload chart
                  documentation below)
                </label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="pa" id="paNo" checked={!paValue} onChange={(e) => changePAValue(false)} />
                <label className="form-check-label fw-bold" htmlFor="paNo">
                  Do not complete on behalf of my office (click “skip for now” below)
                </label>
              </div>

              <div className="card mt-2">
                <div className="card-header bg-success-subtle">
                  <h4 className="text-black mb-0">
                    <i className="bi bi-cloud-arrow-up-fill"></i>{' '}
                    Upload Patient Chart
                  </h4>
                </div>

                <div className="card-body">
                  <div className="input-group mb-2">
                    <input
                      type="file" className="form-control" id="fileUpload"
                      onChange={onUplaodCalled} ref={ref}
                    />
                    {uploadFile ? (
                      <button
                        className="btn btn-outline-secondary me-2" onClick={clearDataAndForm} type="button"
                        id="fileUpload" disabled={uploadFileLoading}
                      >
                        Clear
                      </button>

                    ) : null}
                    <button
                      className="btn btn-success rounded-1" type="button"
                      onClick={() => ConfirmAlert("Are you sure you would like to complete on my behalf?\n This action can not be undone.", "completeOnMyBehalf")}
                      disabled={!uploadFile || uploadFileLoading} style={{ color: '#fff', backgroundColor: '#28a745', borderColor: '#28a745' }}
                    >
                      {uploadFileLoading ? (<i className="spinner-border me-2" style={{
                        width: '16px',
                        height: '16px',
                        fontSize: '9px',
                      }}></i>) : null}
                      Upload
                    </button>
                  </div>
                  {uploadFileInputError && <p className="d-block text-danger mb-0">{uploadFileInputError}</p>}
                  <small className="d-block" style={{ fontSize: '16px' }}>
                    Click here to upload the patient's chart. Currently we accept .txt, .pdf, .tiff, .doc, .ccd, .cda,
                    .ccr, .ccda and image files.
                  </small>
                </div>
              </div>

              {/* <div className="mt-3 text-end">
                <button
                  className="btn btn-blue mx-3 rounded-1" type="button" name="cancel"
                  onClick={hideModal}
                  disabled={isFileUploading}
                >
                  Skip For Now
                </button>

              </div> */}
            </form>

          </div>

          {addSkipForNow ? (
            <div className="modal-footer">
              <button
                type="button" className="btn btn-secondary rounded-1" onClick={() => hideModal(false)}
              >
                Skip for Now
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default UploadClinicalModal
